// The phase strip (spec §13.3, §13.5): the strategy's phases end to end, to
// each race on it, sized by their weeks, today marked. Draft phases (a
// creation run still writing, or a proposal not yet accepted) are dashed;
// provisional ones, whose dates code hasn't fitted to the race yet, are
// faded and say so. The layout is timeline.js's; this only draws it.
import React from 'react';
import { Flag, Star } from 'lucide-react';
import { cn } from '../../lib/utils';
import { layoutTimeline, windowLabel } from '../../v2/timeline';

// One colour per PhaseKind (Figma "Season" / the Plan's phase row): the phase's
// zone, so Base reads aerobic, Build tempo, Peak threshold and Taper easy.
export const KIND_BAR = {
  base: 'bg-zone-aerobic',
  build: 'bg-zone-tempo',
  race_specific: 'bg-zone-tempo',
  peak: 'bg-zone-threshold',
  taper: 'bg-zone-easy',
  race: 'bg-zone-vo2max',
  recovery: 'bg-zone-easy',
  transition: 'bg-border-strong',
};
// The same colours as a tinted pill (the compact row on the Plan and on a proposal).
const KIND_TINT = {
  base: 'bg-zone-aerobic/20', build: 'bg-zone-tempo/20', race_specific: 'bg-zone-tempo/20', peak: 'bg-zone-threshold/20',
  taper: 'bg-zone-easy/20', race: 'bg-zone-vo2max/20', recovery: 'bg-zone-easy/20', transition: 'bg-border-strong/40',
};
// Kept for the season's phase tabs.
export const KIND_CLASSES = Object.fromEntries(Object.keys(KIND_TINT).map((k) => [k, `${KIND_TINT[k]} text-text-primary border-border-default`]));
const barClass = (kind) => KIND_BAR[kind] || 'bg-border-strong';
const tintClass = (kind) => KIND_TINT[kind] || 'bg-border-strong/40';

/**
 * Where each race's label sits under the strip (`lefts`: the races' positions,
 * in %, in order): each gets the room up to halfway to its neighbours, and
 * within it is centred on its race, or held to the race's side near an end of
 * that room. A race at the end of its plan was centred on the edge and ran
 * past the card (a horizontal scroll in the coach panel, 2026-10-29), and a
 * long title ran over the race beside it.
 */
export function raceLabelStyles(lefts) {
  const at = lefts.map((l) => Math.min(Math.max(Number(l) || 0, 0), 100));
  const r2 = (n) => Math.round(n * 100) / 100;
  return at.map((x, i) => {
    const lo = i > 0 ? (at[i - 1] + x) / 2 : 0;
    const hi = i < at.length - 1 ? (x + at[i + 1]) / 2 : 100;
    const frac = hi > lo ? (x - lo) / (hi - lo) : 0.5;
    if (frac >= 0.7) return { right: `${r2(100 - x)}%`, maxWidth: `${r2(x - lo)}%` };
    if (frac <= 0.3) return { left: `${r2(x)}%`, maxWidth: `${r2(hi - x)}%` };
    return { left: `${r2(x)}%`, transform: 'translateX(-50%)', maxWidth: `${r2(2 * Math.min(x - lo, hi - x))}%` };
  });
}

export default function PhaseStrip({ phases, races = [], today = null, draft = false, compact = false }) {
  const layout = layoutTimeline(phases, races, today);
  if (!layout.segments.length) return null;
  const provisional = layout.segments.some((s) => s.provisional);
  const raceStyles = raceLabelStyles(layout.races.map((r) => r.left));
  const title = (s) => `${s.name} · ${windowLabel(s.start_date, s.end_date)}${s.weeks ? ` · ${s.weeks} weeks` : ''}${s.provisional ? ' (dates not final yet)' : ''}`;
  return (
    <div className="w-full" role="group" aria-label={draft ? 'Proposed phases' : 'Your phases'}>
      {compact ? (
        <div className="relative h-[22px] w-full">
          {layout.segments.map((s) => (
            <div key={s.id} title={title(s)}
              className={cn('absolute top-0 flex h-full items-center overflow-hidden rounded-md px-2', tintClass(s.kind),
                draft && 'border border-dashed border-border-strong', s.provisional && 'opacity-60',
                s.current && 'ring-1 ring-inset ring-accent-default')}
              style={{ left: `${s.left}%`, width: `calc(${s.width}% - 3px)` }}>
              <span className={cn('truncate text-[11px] leading-none', s.current ? 'font-semibold text-text-primary' : 'font-medium text-text-secondary')}>{s.name}</span>
            </div>
          ))}
          {layout.today != null && (
            <div className="pointer-events-none absolute -top-1 bottom-[-4px] w-0.5 bg-accent-default" style={{ left: `${layout.today}%` }}
              aria-label="Today" />
          )}
        </div>
      ) : (
        <div className="relative h-9 w-full">
          {layout.segments.map((s) => (
            <div key={s.id} title={title(s)} className={cn('absolute top-0 flex flex-col gap-1.5', s.provisional && 'opacity-60')}
              style={{ left: `${s.left}%`, width: `calc(${s.width}% - 4px)` }}>
              <div className={cn('w-full rounded-[5px]', barClass(s.kind), s.current ? 'h-3.5' : 'mt-0.5 h-2.5',
                draft && 'opacity-70 outline outline-1 outline-dashed outline-border-strong')} />
              <span className={cn('truncate type-caption', s.current ? 'font-semibold text-text-primary' : 'text-text-tertiary')}>
                {s.name}{s.weeks ? ` · ${s.weeks} wks` : ''}
              </span>
            </div>
          ))}
          {layout.today != null && (
            <div className="pointer-events-none absolute -top-1 h-5 w-0.5 rounded bg-text-primary" style={{ left: `${layout.today}%` }}
              aria-label="Today" />
          )}
        </div>
      )}
      {layout.races.length > 0 && (
        <div className={cn('relative w-full overflow-hidden', compact ? 'mt-1 h-5' : 'mt-2 h-5')}>
          {layout.races.map((r, i) => (
            <span key={r.id ?? r.date} className="absolute flex items-center gap-0.5 overflow-hidden whitespace-nowrap type-caption text-text-secondary"
              style={raceStyles[i]} title={`${r.title} · ${windowLabel(r.date)}`}>
              {r.priority === 'A' ? <Star size={10} className="shrink-0 fill-zone-threshold text-zone-threshold" /> : <Flag size={10} className="shrink-0 text-text-tertiary" />}
              <span className="min-w-0 max-w-[110px] truncate">{r.title}</span>
            </span>
          ))}
        </div>
      )}
      {provisional && (
        <p className="m-0 mt-1 type-caption text-text-tertiary">Dates so far: your coach fits them to your race next.</p>
      )}
    </div>
  );
}
